// A monthly retainer's extra charges — income beyond the monthly fee — and where one is added.
//
// AN EXTRA CHARGE is counted inside the retainer itself (the additional PR item the client
// paid for, with no cost behind it): in its revenue, its profit and its split in the
// settlement. It is owed from the moment it is recorded, and appears on the money screen
// like every other amount. It is NOT a job billing its own income — that is a separate
// income unit with its own profit, meant for brokerage with costs of its own.
//
// A ONE-OFF PROJECT HAS NO SUCH CARD: the server sends no `charges` for it. Its price is
// its own automatic charge, paid from the money screen, and income beyond it comes from a
// job charging the client more than it costs us — a client-borne cost with its charge.
//
// Every figure is the server's (retainers/charges). Collected and owed are not repeated
// here: the money status line above states them for the whole engagement.
import { useState } from 'react';
import he from '../../i18n/he';
import DataTable from '../../components/DataTable';
import Money from '../../components/Money';
import DateText from '../../components/DateText';
import Button from '../../components/Button';
import ChargeState from '../../components/ChargeState';
import PaymentModal from '../../components/PaymentModal';
import RecordChargeModal from '../../components/RecordChargeModal';
import { useAlerts } from '../../context/AlertsContext';

const t = he.projects;
const b = he.collections.board;

export default function ChargesCard({ retainer, onChange }) {
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState(null);
  const alerts = useAlerts();
  const charges = retainer.charges;
  if (!charges) return null;
  const done = () => {
    setAdding(false); setPaying(null); onChange();
    if (alerts) alerts.reload();
  };
  // The recording dialog's view of this retainer — the same card shape the money screen
  // hands it, so the dialog cannot tell which screen it was opened from.
  const card = {
    kind: 'retainer', id: retainer.id, title: retainer.title, clientName: retainer.client_name,
    issuingPartnerId: retainer.issuing_partner_id, billing: retainer.billing_mode,
  };

  const columns = [
    { key: 'what', label: t.extraDescription, primary: true, render: (r) => r.description },
    { key: 'issueDate', label: b.date, render: (r) => <DateText value={r.issueDate} /> },
    { key: 'amount', label: b.amount, className: 'num', render: (r) => <Money value={r.amount} /> },
    { key: 'state', label: '', render: (r) => <ChargeState row={r} /> },
    { key: 'pay', label: '', action: true,
      render: (r) => (r.collectible
        ? <Button small onClick={() => setPaying(r)}>{b.recordPayment}</Button>
        : null) },
  ];

  return (
    <div className="card w-section">
      <div className="w-head">
        <h2>{t.extras}</h2>
        <Button small variant="ghost" onClick={() => setAdding(true)}>{t.addExtra}</Button>
      </div>
      <DataTable columns={columns} rows={charges.extras} empty={{ text: t.noExtras }} />
      {charges.extras.length > 1 && (
        <p className="muted">{t.total} <strong><Money value={charges.extrasTotal} /></strong></p>
      )}
      {adding && <RecordChargeModal card={card} onClose={() => setAdding(false)} onSaved={done} />}
      {paying && (
        <PaymentModal
          invoice={{
            id: paying.invoiceId, balance: paying.balance, paid: paying.paid,
            issuingPartnerId: paying.issuingPartnerId,
          }}
          onClose={() => setPaying(null)}
          onSaved={done}
        />
      )}
    </div>
  );
}
